import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import SearchIcon from "../../shared/Icons/SearchIcon";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import "../../TeamManager/styles/teamManagerStyles.css";

const AddVenueModal = ({ isOpen, onClose, onAddVenue, onManualEntry, venueOwnerships }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const existingVenueIds = (venueOwnerships || [])
    .filter((vo) => vo.venueable_type === "Venue")
    .map((vo) => vo.venueable.id);

  const fetchVenues = useCallback(
    debounce((query) => {
      setLoading(true);
      axios
        .get("/venues/search", {
          params: { name: query, omit_ids: existingVenueIds },
        })
        .then(({ data }) => {
          setResults(data);
          setShowResults(true);
        })
        .finally(() => setLoading(false));
    }, 250),
    [existingVenueIds.join(",")]
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (isOpen) {
      setSearchTerm("");
      setResults([]);
      setSelectedVenue(null);
      setShowResults(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.length > 0) {
      fetchVenues(searchTerm);
    } else {
      setResults([]);
      setShowResults(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = (venue) => {
    setSelectedVenue(venue);
    setSearchTerm(venue.name);
    setShowResults(false);
  };

  const handleSubmit = () => {
    if (selectedVenue) {
      onAddVenue(selectedVenue.id, "Venue");
      onClose();
    }
  };

  return (
    <div className="team-modal-overlay">
      <div
        className="team-modal"
        style={{ maxWidth: 560, width: "90%", padding: 0 }}
      >
        <div className="team-modal-header">
          <h3 className="team-modal-title">Add a venue</h3>
          <button className="team-modal-close" onClick={onClose}>
            <XCloseIcon width="20" height="20" />
          </button>
        </div>

        <div className="team-modal-body">
          <div style={{ marginBottom: 6 }}>
            <label
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "var(--sl-text-secondary, #414651)",
                display: "block",
              }}
            >
              Search
            </label>
          </div>

          <div style={{ position: "relative" }} ref={resultsRef}>
            <div className="team-search-wrapper" style={{ width: "100%" }}>
              <span className="team-search-icon"><SearchIcon width="16" height="16" /></span>
              <input
                ref={inputRef}
                type="text"
                className="team-search-input"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedVenue(null);
                }}
                onFocus={() => {
                  if (results.length > 0) setShowResults(true);
                }}
              />
              <span className="team-search-shortcut">{"\u2318"}K</span>
            </div>

            {showResults && results.length > 0 && (
              <div
                style={{
                  marginTop: 4,
                  marginBottom: 30,
                  background: "var(--sl-bg-primary, #ffffff)",
                  border: "1px solid var(--sl-border-secondary, #e9eaeb)",
                  borderRadius: 8,
                  boxShadow: "var(--sl-shadow-lg, 0 4px 16px rgba(0, 0, 0, 0.12))",
                  maxHeight: 240,
                  overflowY: "auto",
                }}
              >
                {results.map((venue) => {
                  const cityState = [venue.city, venue.state]
                    .filter(Boolean)
                    .join(", ");
                  const capacityStr =
                    venue.capacity && venue.capacity !== 0
                      ? ` (Cap: ${venue.capacity.toLocaleString()})`
                      : "";

                  return (
                    <button
                      key={venue.id}
                      type="button"
                      onClick={() => handleSelect(venue)}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "10px 16px",
                        background:
                          selectedVenue && selectedVenue.id === venue.id
                            ? "var(--sl-bg-secondary, #fafafa)"
                            : "none",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: "'Open Sans', sans-serif",
                        fontSize: 14,
                        color: "var(--sl-text-primary, #181d27)",
                        borderBottom: "1px solid var(--sl-border-subtle, #f0f0f0)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "var(--sl-bg-secondary, #fafafa)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          selectedVenue && selectedVenue.id === venue.id
                            ? "var(--sl-bg-secondary, #fafafa)"
                            : "transparent")
                      }
                    >
                      <div style={{ fontWeight: 500 }}>{venue.name}</div>
                      {cityState && (
                        <div
                          style={{ fontSize: 12, color: "var(--sl-text-tertiary, #535862)", marginTop: 2 }}
                        >
                          {cityState}
                          {capacityStr}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {showResults && searchTerm.length > 0 && results.length === 0 && !loading && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  marginTop: 4,
                  background: "var(--sl-bg-primary, #ffffff)",
                  border: "1px solid var(--sl-border-secondary, #e9eaeb)",
                  borderRadius: 8,
                  boxShadow: "var(--sl-shadow-lg, 0 4px 16px rgba(0, 0, 0, 0.12))",
                  padding: "16px",
                  textAlign: "center",
                  fontSize: 14,
                  color: "var(--sl-text-tertiary, #535862)",
                }}
              >
                No venues found
              </div>
            )}
          </div>

          <div style={{ marginTop: 12, fontSize: 14, color: "var(--sl-text-tertiary, #535862)" }}>
            Can&apos;t find your venue?{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onManualEntry();
              }}
              style={{ color: "var(--sl-text-primary, #181d27)", textDecoration: "underline" }}
            >
              Enter your details manually.
            </a>
          </div>
        </div>

        <div className="team-modal-footer">
          <button className="team-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="team-btn-primary"
            disabled={!selectedVenue}
            onClick={handleSubmit}
            style={!selectedVenue ? { opacity: 0.4, cursor: "not-allowed" } : {}}
          >
            Add Venue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVenueModal;
